import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { redactParams, redactUrl } from '../utils/redact-url';

/**
 * Deep Link Service: the URLs the OS hands the app (`appUrlOpen`).
 *
 * Two kinds arrive:
 *  - https links on the web app host (iOS Universal Links / Android App Links,
 *    docs/plans/EMAIL-APP-LINKS.md): every email link into the app. The
 *    origin is stripped and the path opened as-is, for the claimed paths only.
 *  - nvcapp:// (the custom scheme): mail sent before the apex links, and the
 *    Google OAuth return. Kept registered and routed below.
 */

/**
 * The web app hosts whose links open the app. The OS only delivers a host the
 * build claims (the iOS entitlement; Android's appLinkHost, empathease.app
 * unless a staging build overrides it), so listing staging here never makes a
 * production build open staging links.
 */
export const APP_LINK_ORIGINS = [
  'https://empathease.app',
  'https://staging-frontend.empathease.app',
];

/**
 * The paths the apps claim: the same set as server/app/Support/AppLinks.php,
 * the association file and the Android intent-filter (a vitest and a Pest
 * test keep them equal). Public pages (/, /learn, /login, /u/…) stay web.
 */
export const APP_LINK_PATH_PREFIXES = ['/app/', '/verify-email/'];
export const APP_LINK_EXACT_PATHS = ['/reset-password'];

/** The in-app path for an https app link, or null when it is not one. */
export function appLinkPath(url: URL): string | null {
  if (!APP_LINK_ORIGINS.includes(url.origin)) return null;
  const path = url.pathname;
  const claimed =
    APP_LINK_EXACT_PATHS.includes(path) ||
    APP_LINK_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
  return claimed ? `${path}${url.search}` : null;
}

type NavigateFunction = (path: string) => unknown;
type DeepLinkListener = (url: string) => void;

interface AppUrlOpenEvent {
  url: string;
}

export class DeepLinkService {
  private listeners: DeepLinkListener[];
  private isInitialized: boolean;
  /**
   * When each URL was last routed. Only to swallow the cold-start pair: the
   * OS hands the launch URL over twice (the appUrlOpen event and
   * getLaunchUrl, handled a second later), and that pair opens the page once.
   * It must not swallow a real second tap: a set that was never cleared
   * ignored the same link tapped again while the process lived (backlog
   * c-082). A process sees a handful of links, so nothing is pruned.
   */
  private handledAt = new Map<string, number>();

  /**
   * An appUrlOpen of a URL routed this recently is the other half of a
   * double delivery, not a tap: a person does not re-tap the link that just
   * opened the page within seconds.
   */
  static readonly REPEAT_WINDOW_MS = 5_000;

  constructor() {
    this.listeners = [];
    this.isInitialized = false;
  }

  /**
   * Initialize deep link handling
   * @param {Function} navigate - React Router navigate function
   */
  initialize(navigate: NavigateFunction): void {
    if (this.isInitialized || !Capacitor.isNativePlatform()) {
      return;
    }

    console.log('Initializing deep link service...');

    // Listen for app URL open events
    void App.addListener('appUrlOpen', (event: AppUrlOpenEvent) => {
      console.log('Deep link received:', redactUrl(event.url));
      this.handleDeepLink(event.url, navigate);
    });

    // Check if app was opened with a URL
    this.checkInitialUrl(navigate).catch(() => {});

    this.isInitialized = true;
  }

  /**
   * Check if app was opened with a URL (cold start)
   * @param {Function} navigate - React Router navigate function
   */
  async checkInitialUrl(navigate: NavigateFunction): Promise<void> {
    try {
      const result = await App.getLaunchUrl();
      if (result?.url) {
        console.log('App launched with URL:', redactUrl(result.url));
        // Add a small delay to ensure React Router is ready
        setTimeout(() => {
          this.handleDeepLink(result.url!, navigate, 'launch');
        }, 1000);
      }
    } catch (error) {
      console.error('Error getting launch URL:', error);
    }
  }

  /**
   * Handle incoming deep links
   * @param {string} url - The deep link URL
   * @param {Function} navigate - React Router navigate function
   * @param source - 'launch' for getLaunchUrl's copy of the launch URL
   */
  handleDeepLink(
    url: string,
    navigate: NavigateFunction,
    source: 'event' | 'launch' = 'event',
  ): void {
    if (this.isRepeatDelivery(url, source)) {
      console.log('Deep link already handled, skipping:', redactUrl(url));
      return;
    }

    this.handledAt.set(url, Date.now());

    try {
      // The URL carries the link's secret (token, signature): logs get it
      // through redactUrl only (backlog c-059).
      console.log('Processing deep link:', redactUrl(url));

      // Parse the URL
      const urlObj = new URL(url);

      // An https link: a web app host's claimed path opens as-is; anything
      // else (a foreign origin, a path the app does not claim) goes home.
      if (urlObj.protocol === 'https:' || urlObj.protocol === 'http:') {
        navigate(appLinkPath(urlObj) ?? '/');
        return;
      }

      const path = urlObj.pathname;
      const params = new URLSearchParams(urlObj.search);

      console.log('Deep link path:', path);
      console.log('Deep link params:', redactParams(params));

      // The verification mail's app link: /verify-email/:id/:hash?expires&signature.
      // The page at that route calls the API with the signed query. Matched
      // before the switch because the exact-path case below never matched it
      // (it sent the user to '/' and nothing was verified).
      if (/^\/verify-email\/[^/]+\/[^/]+$/.test(path)) {
        navigate(`${path}${urlObj.search}`);
        return;
      }

      // Handle different deep link paths
      switch (path) {
        case '/reset-password':
          this.handlePasswordReset(params, navigate);
          break;

        case '/verify-email':
          this.handleEmailVerification(params, navigate);
          break;

        case '/login':
          this.handleLoginRedirect(params, navigate);
          break;

        default:
          console.log('Unknown deep link path:', path);
          navigate('/');
          break;
      }
    } catch (error) {
      console.error('Error handling deep link:', error);
      navigate('/');
    }
  }

  /**
   * Whether this delivery is the twin of one already routed. getLaunchUrl's
   * copy is a twin whenever its appUrlOpen ran first, however slow the cold
   * start (a process launches once). An appUrlOpen is a twin only inside
   * REPEAT_WINDOW_MS of the last routing (the launch copy ran first); after
   * that it is the person tapping the link again, and it opens again.
   */
  private isRepeatDelivery(url: string, source: 'event' | 'launch'): boolean {
    const last = this.handledAt.get(url);
    if (last === undefined) return false;
    if (source === 'launch') return true;
    return Date.now() - last < DeepLinkService.REPEAT_WINDOW_MS;
  }

  /**
   * Handle password reset deep links
   * @param {URLSearchParams} params - URL parameters
   * @param {Function} navigate - React Router navigate function
   */
  handlePasswordReset(params: URLSearchParams, navigate: NavigateFunction): void {
    const token = params.get('token');
    const email = params.get('email');

    if (token && email) {
      console.log('Navigating to password reset with token');
      navigate(
        `/reset-password?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`,
      );
    } else {
      console.error('Missing token or email in password reset deep link');
      navigate('/forgot-password');
    }
  }

  /**
   * Handle email verification deep links
   * @param {URLSearchParams} params - URL parameters
   * @param {Function} navigate - React Router navigate function
   */
  handleEmailVerification(params: URLSearchParams, navigate: NavigateFunction): void {
    const token = params.get('token');
    const userId = params.get('user');

    if (token && userId) {
      console.log('Handling email verification deep link');
      navigate(
        `/verify-email?token=${encodeURIComponent(token)}&user=${encodeURIComponent(userId)}`,
      );
    } else {
      console.error('Missing token or user in email verification deep link');
      navigate('/');
    }
  }

  /**
   * Handle login redirect deep links (for errors, etc.)
   * @param {URLSearchParams} params - URL parameters
   * @param {Function} navigate - React Router navigate function
   */
  handleLoginRedirect(params: URLSearchParams, navigate: NavigateFunction): void {
    const error = params.get('error');

    if (error) {
      console.log('Handling login error deep link:', error);
      navigate(`/login?error=${encodeURIComponent(error)}`);
    } else {
      console.log('Handling login redirect deep link');
      navigate('/login');
    }
  }

  /**
   * Add a custom deep link listener
   * @param {Function} callback - Callback function to handle deep links
   */
  addListener(callback: DeepLinkListener): void {
    this.listeners.push(callback);
  }

  /**
   * Remove a deep link listener
   * @param {Function} callback - Callback function to remove
   */
  removeListener(callback: DeepLinkListener): void {
    this.listeners = this.listeners.filter((listener) => listener !== callback);
  }
}

// Export singleton instance
export const deepLinkService = new DeepLinkService();
export default deepLinkService;
